import type {
  ExistingTreeRepairCategory,
  ExistingTreeRepairPlan,
  ExistingTreeRepairPlanItem,
  ExistingTreeRequiredApproval,
} from './ExistingTreeRepairPlan';
import type {
  ExistingTreeAuditSeverity,
} from './ExistingTreeStructuralAudit';

export interface ExistingTreeRepairCategoryGroup {
  category: ExistingTreeRepairCategory;
  itemCount: number;
  items: ExistingTreeRepairPlanItem[];
}

export interface ExistingTreeRepairSeverityGroup {
  severity: ExistingTreeAuditSeverity;
  itemCount: number;
  items: ExistingTreeRepairPlanItem[];
}

export interface ExistingTreeRepairApprovalGroup {
  approval: ExistingTreeRequiredApproval;
  itemCount: number;
  items: ExistingTreeRepairPlanItem[];
}

export interface ExistingTreeRepairPlanGrouping {
  categoryGroups: ExistingTreeRepairCategoryGroup[];
  severityGroups: ExistingTreeRepairSeverityGroup[];
  approvalGroups: ExistingTreeRepairApprovalGroup[];
  executionAllowed: false;
  productionWritesAllowed: false;
}

const CATEGORIES:
  readonly ExistingTreeRepairCategory[] = [
    'REFERENCE_INTEGRITY',
    'PARENT_ROLE_REVIEW',
    'IDENTITY_REVIEW',
    'GENERATION_REVIEW',
    'DISPLAY_PREFERENCE',
  ];

const SEVERITIES:
  readonly ExistingTreeAuditSeverity[] = [
    'blocking',
    'warning',
    'info',
  ];

const APPROVALS:
  readonly ExistingTreeRequiredApproval[] = [
    'TREE_OWNER',
    'CLAIMED_MEMBER',
    'IDENTITY_REVIEW',
    'GOVERNED_OPERATOR_REVIEW',
  ];

export function groupExistingTreeRepairPlan(
  plan: ExistingTreeRepairPlan,
): ExistingTreeRepairPlanGrouping {
  const categoryGroups = CATEGORIES.map(
    (category) => {
      const items = plan.items.filter(
        (item) => item.category === category,
      );

      return {
        category,
        itemCount: items.length,
        items,
      };
    },
  ).filter((group) => group.itemCount > 0);

  const severityGroups = SEVERITIES.map(
    (severity) => {
      const items = plan.items.filter(
        (item) => item.severity === severity,
      );

      return {
        severity,
        itemCount: items.length,
        items,
      };
    },
  ).filter((group) => group.itemCount > 0);

  const approvalGroups = APPROVALS.map(
    (approval) => {
      const items = plan.items.filter(
        (item) =>
          item.requiredApprovals.includes(
            approval,
          ),
      );

      return {
        approval,
        itemCount: items.length,
        items,
      };
    },
  ).filter((group) => group.itemCount > 0);

  return {
    categoryGroups,
    severityGroups,
    approvalGroups,
    executionAllowed: false,
    productionWritesAllowed: false,
  };
}
